/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { ModalError } from '@features/sourcing/productions/components/ModalError';
import {
	ActionIcon,
	Box,
	Center,
	Flex,
	Grid,
	Group,
	Loader,
	Paper,
	SimpleGrid,
	Space,
} from '@mantine/core';

import uploadIcon from '@assets/svg/UploadIcon.svg';
import manualEnterIcon from '@assets/svg/manualEnterIcon.svg';
import { Select, Title } from '@components/v2/ui';
import { DefaultModal } from '@components/v2/ui/DefaultModal';
import {
	TMeasurabilityProject,
	TMeterPoint,
	TSite,
} from '@global-types/api/api';
import { TSelectOption } from '@global-types/index';
import { isNotEmpty, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import useStore from '@store/index';
import { IconPlus } from '@tabler/icons-react';
import {
	cloneDeep,
	convertArrayMeterPointsToLabelValue,
	convertArrayToLabelValue,
	detectSeparator,
	extractColumnNames,
	uuid,
} from '@utils/functions';
import {
	PRODUCTION_CSV_COLUMNS_OPTIONS,
	PRODUCTION_UNITS_OPTIONS,
} from '@utils/options';
import { getUnitLabel } from '@utils/units';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
	lazy,
	Suspense,
	useEffect,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useMeterPoints } from 'shared/api/get-meter-points';
import { useProducers } from 'shared/api/get-producers';
import { useSites } from 'shared/api/get-sites';
import { useImportProductions } from '../../api/import-production-v1';
import {
	TAddProductionForm,
	TManualProduction,
} from '../../types';
import classes from './AddProduction.module.css';
import { Header } from './components/Header';
import { ProductionsListFromFile } from './components/ProductionsListFromFile/ProductionsListFromFile';
import { SelectionCard } from './components/SelectionCard/SelectionCard';
const ImportFileForm = lazy(
	() =>
		import('./components/ImportFileForm/ImportFileForm')
);

const ManualEntryForm = lazy(
	() =>
		import('./components/ManualEntryForm/ManualEntryForm')
);

type TCsvColumnOption = {
	label: string;
	value: string;
	required: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddProduction = () => {
	/* ******************************** HOOKS *********************************** */
	const { t } = useTranslation();

	useEffect(() => {
		const _options: TCsvColumnOption[] =
			PRODUCTION_CSV_COLUMNS_OPTIONS;
		setColumns(_options);
		setAddedProductions([]);
		setIsAddProductionFromFile(false);
		setSelectedColumnsCSV([]);
	}, []);

	/* ******************************** STATES *********************************** */
	const [
		hasMultipleMeterpoints,
		setHasMultipleMeterpoints,
	] = useState(false);

	const {
		setSelectedColumnsCSV,
		setIsAddProductionFromFile,
		productionMethod,
		setProductionMethod,
		setIsAddProduction,
		setFileData,
		fileData,
		setColumns,
		setDelimiterCSV,
		columnsCSV,
		setColumnsCSV,
		selectedColumnsCSV,
		isOpenModalCancelAddProduction,
		setIsOpenModalCancelAddProduction,
		setAddedProductions,
		addedProductions,
		isAddProductionFromFile,
		productionsImportErrors,
		setProductionsImportErrors,
	} = useStore();

	const [isShowModalErrors, setIsShowModalErrors] =
		useState(false);

	const [csvDataObjects, setCsvDataObjects] = useState<
		any[]
	>([]);
	/* ******************************** APIs *********************************** */
	const { data: producersToAddProductions } =
		useProducers();

	const { data: projects } = useMeasurabilities();
	const { data: sites } = useSites();
	const { data: meterPoints } = useMeterPoints();

	const importProductionMutation = useImportProductions({
		mutationConfig: {
			onSuccess: () => {
				setIsAddProduction(false);
				setProductionMethod(null);
				notifications.show({
					title: t('global.success'),
					message: t(
						'productions.notifications.productionSuccessfullyAdded'
					),
					color: 'var(--mantine-color-igluGreen-9)',
				});
			},
			onError: (error: any) => {
				const formattedErrors = Array.isArray(error?.data)
					? error.data.flatMap(({ line, errors }: any) =>
							errors.map((messageError: any) => ({
								message:
									messageError.column +
									' ' +
									messageError.error,
								line,
								id: uuid(),
							}))
						)
					: [];

				setProductionsImportErrors({
					...error,
					data: formattedErrors,
				});
			},
		},
	});

	useEffect(() => {
		if (productionsImportErrors) {
			setIsShowModalErrors(true);
		}
	}, [productionsImportErrors]);

	/* ******************************** Form *********************************** */

	const form = useForm<TAddProductionForm>({
		mode: 'controlled',
		initialValues: {
			producer: '',
			project: '',
			site: '',
			meterPoint: '',
			unitProduction: '',
			productions: [
				{
					quantity: 0,
					startDate: null as Date | null | string,
					endDate: null as Date | null | string,
					conversionFactor: 0,
					proof: '',
					id: crypto.randomUUID(),
				},
			],
		},
		validate: {
			producer: isNotEmpty(
				t('errors.producerIsRequired')
			),
			project: isNotEmpty(t('errors.projectIsRequired')),
			site: isNotEmpty(t('errors.siteIsRequired')),
			meterPoint: isNotEmpty(
				t('errors.meterpointIsRequired')
			),
			unitProduction: isNotEmpty(
				t('global.unitRequired')
			),
		},
	});

	const { onChange: onChangeProducer, ...producerProps } =
		form.getInputProps('producer');
	const { onChange: onChangeProject, ...projectProps } =
		form.getInputProps('project');
	const { onChange: onChangeSite, ...siteProps } =
		form.getInputProps('site');
	const {
		onChange: onChangeMeterPoint,
		...meterPointProps
	} = form.getInputProps('meterPoint');

	/* ******************************** HELPERS *********************************** */
	const parseCSVToObjects = (
		content: string,
		delimiter: string
	) => {
		const lines = content
			.split(/\r?\n/)
			.filter((line) => line.trim() !== '');
		if (lines.length < 2) return [];

		const headers = lines[0]
			.split(delimiter)
			.map((h) => h.replace(/["\r]/g, '').trim());

		return lines.slice(1).map((line) => {
			const values = line.split(delimiter);
			const obj: any = {};
			headers.forEach((header, index) => {
				obj[header] =
					values[index]?.replace(/["\r]/g, '').trim() ||
					'';
			});
			return obj;
		});
	};

	function updateLabelBasedOnUnit(
		_options: any,
		unitProduction: any
	) {
		const cloneOptions = cloneDeep(_options);

		let unitLabel: string = '';

		switch (unitProduction) {
			case 'KWH':
				unitLabel = getUnitLabel('gCO2perKwh');
				break;
			case 'MWH':
				unitLabel = getUnitLabel('gCO2perKwh');
				break;
			case 'KG':
				unitLabel = getUnitLabel('gCO2perKg');
				break;
			case 'kg':
				unitLabel = getUnitLabel('gCO2perKg');
				break;
			case 't':
				unitLabel = getUnitLabel('gCO2perKg');
				break;
			case 'T':
				unitLabel = getUnitLabel('gCO2perKg');
				break;
			default:
		}
		const index = cloneOptions.findIndex(
			(option: any) => option.label === 'conversionFactor'
		);
		if (index !== -1) {
			cloneOptions[index] = {
				..._options[index],
				label: `${cloneOptions[index].label} ${unitLabel}`,
			};
		}

		return cloneOptions;
	}

	const handleUpload = (file: File) => {
		setFileData(file);
		const reader = new FileReader();
		reader.onload = (e: any) => {
			const content = e.target.result;
			const _delimiter: any = detectSeparator(content);

			const parsedData = parseCSVToObjects(
				content,
				_delimiter
			);
			setCsvDataObjects(parsedData);

			setDelimiterCSV(_delimiter);
			const cloneExtractHeaders = [
				...extractColumnNames(content),
			];
			const extractColumnNamesList: any =
				cloneExtractHeaders.map((item: any) => ({
					label: item.replace(/[\r\n]+|"/g, ''),
					value: item.replace(/[\r\n]+|"/g, ''),
				}));

			setColumnsCSV(extractColumnNamesList);

			const isMultipleMeterpoints =
				form.getInputProps('meterPoint').value ===
				'multiplemeterpoint';

			const _options: TCsvColumnOption[] =
				PRODUCTION_CSV_COLUMNS_OPTIONS.filter((item) =>
					isMultipleMeterpoints
						? true
						: item.value !== 'meterPoint'
				);

			const unitProduction = form.getInputProps(
				'unitProduction'
			).value;

			const _columns = updateLabelBasedOnUnit(
				_options,
				unitProduction
			);

			setColumns(_columns);
		};
		reader.readAsText(file);
	};

	const getUnitsOfProductions = (
		options: TSelectOption[]
	) => {
		const _selectedSite = sites.find(
			(_site: any) =>
				_site.id == form.getInputProps('site').value
		);

		if (_selectedSite && _selectedSite.unit == 'KWH') {
			return [
				{ label: 'kilowattheure', value: 'KWH' },
				{ label: 'mégawattheures', value: 'MWH' },
			];
		} else if (
			_selectedSite &&
			_selectedSite.unit == 'KG'
		) {
			return [
				{ label: 'Tonne ', value: 'T' },
				{ label: 'kilogramme ', value: 'KG' },
			];
		} else return options;
	};

	const handleChangeProducer = (value: string | null) => {
		form.setFieldValue('producer', value);
		if (form.getInputProps('project').value) {
			form.setFieldValue('project', null);
			form.setFieldValue('site', null);
			form.setFieldValue('meterPoint', null);
			form.setFieldValue('unitProduction', null);
		}
	};

	const handleChangeProject = (value: string | null) => {
		form.setFieldValue('project', value);
		if (form.getInputProps('site').value) {
			form.setFieldValue('site', null);
			form.setFieldValue('meterPoint', null);
			form.setFieldValue('unitProduction', null);
		}

		const projectSites = sites.filter(
			(_site: any) => _site.project.id === value
		);

		if (
			projectSites &&
			Array.isArray(projectSites) &&
			projectSites.length === 1
		) {
			form.setFieldValue('site', projectSites[0].id);

			const meterPointsSite = meterPoints.filter(
				(_meterpoint: any) =>
					_meterpoint.site === projectSites[0].id
			);

			if (
				meterPointsSite &&
				Array.isArray(meterPointsSite) &&
				meterPointsSite.length === 1
			) {
				form.setFieldValue(
					'meterPoint',
					meterPointsSite[0].id
				);
			}
		}
	};

	const handleChangeSite = (value: string | null) => {
		const meterPointsSite = meterPoints.filter(
			(_meterpoint: any) => _meterpoint.site === value
		);

		if (
			meterPointsSite &&
			Array.isArray(meterPointsSite) &&
			meterPointsSite.length == 1
		) {
			form.setFieldValue(
				'meterPoint',
				meterPointsSite[0].id
			);
		}

		if (
			meterPointsSite &&
			Array.isArray(meterPointsSite)
		) {
			setHasMultipleMeterpoints(
				meterPointsSite.length > 1
			);
		}

		form.setFieldValue('site', value);
	};

	const handleChangeMeterPoint = (
		value: string | null
	) => {
		form.setFieldValue('meterPoint', value);
		if (form.getInputProps('unitProduction').value) {
			form.setFieldValue('unitProduction', null);
		}
	};

	const cleanProductions = (
		productions: TManualProduction[]
	) => {
		return productions.map((production: any) => {
			if (typeof production === 'object')
				for (const propName in production) {
					if (
						production[propName] === null ||
						production[propName] === '' ||
						production[propName] === 0 ||
						production[propName] === undefined
					) {
						delete production[propName];
					}
				}
			return production;
		});
	};

	const convertToCSV = (data: any) => {
		if (!data.length) return '';

		const headers = Object.keys(data[0]);
		const headerRow = headers.join(',');
		const rows = data.map((row: any) =>
			headers
				.map(
					(header) =>
						`"${(row[header] ?? '')
							.toString()
							.replace(/"/g, '""')}"`
				)
				.join(',')
		);

		return [headerRow, ...rows].join('\n');
	};

	/* ******************************** CTA *********************************** */

	/**
	 * Aborts the production creation process and resets the UI state.
	 * * This handler triggers a state update in the global store to exit the
	 * "Add Production" view. It is typically invoked by the Header's
	 * cancellation action to return the user to the previous context
	 * without persisting any unsaved form data.
	 *
	 * @function handleCancel
	 * @memberof AddProduction
	 * @returns {void}
	 */
	const handleCancel = () => {
		setIsOpenModalCancelAddProduction(true);
	};

	/**
	 * Handles the final submission of the production form.
	 * * This method performs the following steps:
	 * 1. Validates the form state.
	 * 2. Prepares metadata (unit, site, or meter point) based on the selection mode.
	 * 3. If the method is 'manual':
	 * - Processes production dates to ensure valid ranges (adds 1 day if start and end dates are identical).
	 * - Generates a CSV file from the production list.
	 * - Wraps everything into a FormData object.
	 * 4. Triggers the asynchronous mutation to import the data.
	 * * @returns {void} Returns early if the form is invalid.
	 */
	const handleConfirm = () => {
		// Validate all form fields
		form.validate();

		// Stop execution if any validation rule fails
		if (!form.isValid()) {
			return;
		}

		// Initialize FormData for multipart/form-data submission
		const body: FormData = new FormData();

		// Find the selected meter point object from the list
		const _meterpoint = meterPoints.find(
			(meterpoint: TMeterPoint) =>
				meterpoint.id ==
				form.getInputProps('meterPoint').value
		);

		// Append unit metadata if a specific meter point is found
		if (_meterpoint) {
			body.append(
				'unit',
				form.getInputProps('unitProduction').value
			);
		}

		// Check if the user selected the "multiple meter points" option
		const isMultipleMeterpoints =
			form.getInputProps('meterPoint').value ===
			'multiplemeterpoint';

		// Append single meter point ID if not in multiple mode
		if (!isMultipleMeterpoints) {
			body.append(
				'meterPoint',
				form.getInputProps('meterPoint').value
			);
		}

		// Append both unit and site ID when handling multiple meter points
		if (isMultipleMeterpoints) {
			body.append(
				'unit',
				form.getInputProps('unitProduction').value
			);
			body.append(
				'site',
				form.getInputProps('site').value
			);
		}

		const hasStartDate =
			selectedColumnsCSV.filter(
				(objet: any) => objet.value === 'startDate'
			).length == 1;

		const hasQuantity =
			selectedColumnsCSV.filter(
				(objet: any) => objet.value === 'quantity'
			).length == 1;

		if (!hasStartDate) {
			form.setFieldError('entity-startDate', ' ');
			form.setFieldError(
				'row-startDate',
				'startDate is required'
			);
		}
		if (!hasQuantity) {
			form.setFieldError('entity-quantity', ' ');
			form.setFieldError(
				'row-quantity',
				'quantity is required'
			);
		}

		// Execute logic specific to manual data entry
		if (productionMethod === 'manual') {
			// Set CSV configuration
			body.append('delimiter', ',');

			// Generate a unique filename with UTC timestamp
			body.append(
				'filename',
				`productions_manual_${dayjs().utc().toISOString()}.csv`
			);

			// Retrieve the list of productions from form state
			const productionsList = addedProductions;

			// Transform dates for CSV compatibility
			const updatedEnddateProductionsList =
				productionsList.map((item: TManualProduction) => {
					const { id, ...rest } = item;
					// Format start date to standard YYYY-MM-DD
					const newStartDate = dayjs(
						rest.startDate
					).format('YYYY-MM-DD');

					if (rest.startDate && item.endDate) {
						const startDate = dayjs(item.startDate);
						const endDate = dayjs(item.endDate);

						// Check if start and end are the same day
						const isSameDates = startDate.isSame(
							endDate,
							'day'
						);

						// Add 1 day if dates are same to ensure valid range
						const newEndDate = endDate
							.add(isSameDates ? 1 : 0, 'day')
							.format('YYYY-MM-DD');

						// Return updated item with new Date objects
						return {
							...item,
							startDate: new Date(newStartDate),
							endDate: new Date(newEndDate),
						};
					}

					// Return item with formatted start date only
					return {
						...item,
						startDate: new Date(newStartDate),
					};
				});

			// Create a Blob from the converted CSV string
			const blobCsvData = new Blob(
				[
					convertToCSV(
						cleanProductions(
							updatedEnddateProductionsList
						)
					),
				],
				{ type: 'text/csv;charset=utf-8;' }
			);

			// Wrap the Blob into a File object with a name
			const namedFile = new File(
				[blobCsvData],
				`productions_manual_${dayjs().utc().toISOString()}.csv`,
				{ type: 'text/csv;charset=utf-8;' }
			);

			// Append the final CSV file to the request body
			body.append('file', namedFile);

			// Trigger the mutation to send data to the server
			importProductionMutation.mutate(body);
			return;
		}

		if (productionMethod === 'import') {
			if (hasStartDate && hasQuantity) {
				const cloneColumnsCSV = JSON.parse(
					JSON.stringify(columnsCSV)
				);
				const cloneSelectedColumnsCSV = JSON.parse(
					JSON.stringify(selectedColumnsCSV)
				);

				cloneColumnsCSV.forEach(
					(element: any, index: number) => {
						const valueToFind = element.value;
						let found = false;

						cloneSelectedColumnsCSV.forEach(
							(item: any) => {
								if (valueToFind === item.columnsCSV) {
									element.label = item.label;
									element.value = item.value;
									found = true;
								}
							}
						);
						if (!found) {
							element.label = 'ignore' + index;
							element.value = 'ignore' + index;
						}
					}
				);

				const parseHeaders = cloneColumnsCSV
					.map((_column: any) => _column.value)
					.toString();
				body.append('headers', parseHeaders);

				if (fileData) {
					const filename =
						fileData &&
						fileData.name &&
						fileData.name.replace(/\.[^/.]+$/, '');

					body.append('filename', filename);
					body.append('file', fileData);
				}

				importProductionMutation.mutate(body);
			}
			return;
		}
	};

	/* ******************************** RENDER *********************************** */

	return (
		<Paper className={clsx(classes['main-layout'])}>
			<ModalError
				type="delete"
				data={
					productionsImportErrors &&
					productionsImportErrors.data &&
					Array.isArray(productionsImportErrors.data) &&
					productionsImportErrors.data.length > 0 &&
					productionsImportErrors.data
				}
				message={
					productionsImportErrors &&
					productionsImportErrors?.message &&
					productionsImportErrors.message
				}
				onConfirm={() => {}}
				onClose={() => {
					setIsShowModalErrors(false);
					setProductionsImportErrors(null);
				}}
				onRemove={(_error) => {
					const updatedErrors =
						productionsImportErrors.data.filter(
							(_err: any) => _err.id !== _error.id
						);
					setProductionsImportErrors({
						...productionsImportErrors,
						data: updatedErrors,
					});
				}}
				opened={isShowModalErrors}
			/>

			<DefaultModal
				opened={isOpenModalCancelAddProduction}
				onConfirm={() => {
					setIsAddProduction(false);
					setProductionMethod(null);
					setIsOpenModalCancelAddProduction(false);
				}}
				onClose={() => {
					setIsOpenModalCancelAddProduction(false);
				}}
				title={t('productions.backToAllProductionsSure')}
			/>

			<Header
				onCancel={handleCancel}
				onConfirm={handleConfirm}
				title={t('productions.addProduction')}
				isLoading={importProductionMutation.isPending}
			/>

			<div
				style={{
					padding: '0px 32px',
				}}
			>
				<Grid mt={'32px'} mb={'16px'}>
					<Grid.Col span={{ base: 12, md: 3, lg: 3 }}>
						<div></div>
					</Grid.Col>
					<Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
						<Flex gap="xs" wrap="nowrap" flex={1}>
							<Select
								w={'100%'}
								searchable
								label={t('global.producer')}
								withAsterisk
								placeholder={t(
									'placeholders.selectProducer'
								)}
								data={convertArrayToLabelValue(
									producersToAddProductions
								)}
								onChange={handleChangeProducer}
								{...producerProps}
							/>

							<ActionIcon
								disabled
								variant="outline"
								size="48px"
								radius="xl"
								onClick={() => null}
								style={{
									border: '1px solid #ced4da',
									marginTop: '25px',
								}}
							>
								<IconPlus size={24} color="#1D3965" />
							</ActionIcon>
						</Flex>
					</Grid.Col>
					<Grid.Col span={{ base: 12, md: 3, lg: 3 }}>
						<div></div>
					</Grid.Col>
				</Grid>

				<SimpleGrid
					spacing="24px"
					cols={{ base: 1, sm: 2, lg: 4 }}
				>
					<Group
						align="flex-start"
						gap="xs"
						wrap="nowrap"
					>
						<Box style={{ flex: 1 }}>
							<Select
								disabled={!form.isValid('producer')}
								searchable
								label={t('global.project')}
								withAsterisk
								placeholder={t(
									'placeholders.selectProject'
								)}
								data={convertArrayToLabelValue(
									projects.filter(
										(item: TMeasurabilityProject) =>
											item.entity.id ==
											form.getInputProps('producer').value
									)
								)}
								onChange={handleChangeProject}
								{...projectProps}
							/>
						</Box>
						<ActionIcon
							variant="outline"
							disabled
							size="48px"
							radius="xl"
							onClick={() => null}
							style={{
								border: '1px solid #ced4da',
								marginTop: '25px',
							}}
						>
							<IconPlus size={24} color="#1D3965" />
						</ActionIcon>
					</Group>
					<Group
						align="flex-start"
						gap="xs"
						wrap="nowrap"
					>
						<Box style={{ flex: 1 }}>
							<Select
								disabled={!form.isValid('project')}
								searchable
								label={t('global.site')}
								withAsterisk
								placeholder={t('placeholders.selectSite')}
								data={convertArrayToLabelValue(
									sites.filter(
										(item: TSite) =>
											item.project.id ==
											form.getInputProps('project').value
									)
								)}
								onChange={handleChangeSite}
								{...siteProps}
							/>
						</Box>
						<ActionIcon
							variant="outline"
							disabled
							size="48px"
							radius="xl"
							onClick={() => null}
							style={{
								border: '1px solid #ced4da',
								marginTop: '25px',
							}}
						>
							<IconPlus size={24} color="#1D3965" />
						</ActionIcon>
					</Group>
					<Group
						align="flex-start"
						gap="xs"
						wrap="nowrap"
					>
						<Box style={{ flex: 1 }}>
							<Select
								disabled={!form.isValid('site')}
								searchable
								label={t('global.meterPoint')}
								withAsterisk
								placeholder={t(
									'placeholders.selectMeterPoint'
								)}
								data={[
									...convertArrayMeterPointsToLabelValue(
										(meterPoints || []).filter(
											(item: any) =>
												item.site === form.values.site
										)
									),
									...(hasMultipleMeterpoints
										? [
												{
													label: t('global.fromFile'),
													value: 'multiplemeterpoint',
												},
											]
										: []),
								]}
								onChange={handleChangeMeterPoint}
								{...meterPointProps}
							/>
						</Box>
						<ActionIcon
							variant="outline"
							disabled
							size="48px"
							radius="xl"
							onClick={() => null}
							style={{
								border: '1px solid #ced4da',
								marginTop: '25px',
							}}
						>
							<IconPlus size={24} color="#1D3965" />
						</ActionIcon>
					</Group>
					<Group
						align="flex-start"
						gap="xs"
						wrap="nowrap"
					>
						<Box style={{ flex: 1 }}>
							<Select
								disabled={!form.isValid('meterPoint')}
								searchable
								label={t('global.unitProduction')}
								withAsterisk
								placeholder={t(
									'placeholders.selectUnitProduction'
								)}
								data={getUnitsOfProductions(
									PRODUCTION_UNITS_OPTIONS
								)}
								{...form.getInputProps('unitProduction')}
							/>
						</Box>
						<ActionIcon
							variant="outline"
							disabled
							size="48px"
							radius="xl"
							onClick={() => null}
							style={{
								border: '1px solid #ced4da',
								marginTop: '25px',
							}}
						>
							<IconPlus size={24} color="#1D3965" />
						</ActionIcon>
					</Group>
				</SimpleGrid>
			</div>
			<Space h="48px" />
			{productionMethod === null && (
				<div>
					<Title
						mt={'12px'}
						mb={'16px'}
						order={6}
						c={'#1D3965'}
						ta={'center'}
					>
						Choisissez votre méthode d'ajout
					</Title>
					<SimpleGrid
						cols={{ base: 1, sm: 2 }}
						spacing="32px"
						verticalSpacing="xl"
						w="100%"
						pl={'64px'}
						pr={'64px'}
					>
						<SelectionCard
							title={t('global.importFile')}
							description={t(
								'global.descriptionImportFile'
							)}
							icon={uploadIcon}
							onClick={() =>
								setProductionMethod('import')
							}
						/>
						<SelectionCard
							title={t('global.enterManually')}
							description={t(
								'global.descriptionEnterManually'
							)}
							icon={manualEnterIcon}
							onClick={() =>
								setProductionMethod('manual')
							}
						/>
					</SimpleGrid>
				</div>
			)}

			{productionMethod === 'import' && (
				<Suspense
					fallback={
						<Center mt="xl">
							<Loader size="md" />
						</Center>
					}
				>
					<ImportFileForm
						onPrevious={() => {
							setProductionMethod(null);
							setIsAddProductionFromFile(false);

							setCsvDataObjects([]);
						}}
						onDrop={(files) => {
							handleUpload(files[0]);
						}}
						form={form}
					/>

					{isAddProductionFromFile &&
						csvDataObjects?.length > 0 && (
							<ProductionsListFromFile
								selectedColumns={selectedColumnsCSV}
								addedProductions={csvDataObjects}
							/>
						)}
				</Suspense>
			)}

			{productionMethod === 'manual' && (
				<Suspense
					fallback={
						<Center mt="xl">
							<Loader size="md" />
						</Center>
					}
				>
					<ManualEntryForm
						onPrevious={() => {
							setProductionMethod(null);
						}}
						form={form}
					/>
				</Suspense>
			)}
		</Paper>
	);
};
